// Franchisee Public Landing — /franchisee/:pid (shareable)
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';

const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL || ''}${u}`);

export default function FranchiseeLanding() {
  const { pid } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', investment_budget: '', message: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get(`/franchisee/${pid}`); setPost(r.data); }
    catch (e) { toast.error('Not found'); }
    finally { setLoading(false); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success('📋 Link copied'); } catch {}
  };
  const waShare = () => {
    const msg = `🏢 Franchise Opportunity: *${post?.title}*\n${post?.tagline || ''}\n\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const submit = async () => {
    if (!localStorage.getItem('token')) { window.location.href = '/login'; return; }
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Name + phone required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/franchisee/apply', { post_id: pid, ...form });
      toast.success('✅ Application submitted');
      if (r.data?.whatsapp_link) {
        if (window.confirm('Franchise owner ko WhatsApp pe turant notify karna hai?')) {
          window.open(r.data.whatsapp_link, '_blank');
        }
      }
      setShowApply(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">Loading…</div>;
  if (!post) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">Not found</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-amber-950/40 to-slate-950 text-white pb-24" data-testid="fr-landing">
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-3">
        <a href="/marketplace" className="text-xs text-amber-300 font-black">← Marketplace</a>
        <div className="rounded-2xl bg-slate-900/80 border-2 border-amber-400/40 overflow-hidden">
          {(post.images || [])[0] && <img src={abs(post.images[0])} alt="" className="w-full h-56 object-cover" />}
          <div className="p-4 space-y-3">
            <div>
              <div className="text-2xl font-black" data-testid="fr-title">{post.title}</div>
              {post.tagline && <div className="text-sm opacity-80">{post.tagline}</div>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {post.category && <span className="px-2 py-1 rounded bg-slate-800 text-[11px]">🏷 {post.category}</span>}
              {post.city && <span className="px-2 py-1 rounded bg-slate-800 text-[11px]">📍 {post.city}</span>}
              {post.investment_range && <span className="px-2 py-1 rounded bg-emerald-600/30 text-emerald-200 font-black text-[11px]">💰 {post.investment_range}</span>}
              {post.roi && <span className="px-2 py-1 rounded bg-purple-600/30 text-purple-200 font-black text-[11px]">📈 {post.roi}</span>}
            </div>
            {post.description && <div className="text-sm opacity-90 whitespace-pre-wrap">{post.description}</div>}

            {(post.images || []).length > 1 && (
              <div className="grid grid-cols-3 gap-1.5">
                {post.images.slice(1).map((img, i) => (
                  <img key={i} src={abs(img)} alt="" className="w-full h-24 object-cover rounded" />
                ))}
              </div>
            )}

            {(post.videos || []).map((v, i) => (
              <video key={i} src={abs(v)} controls className="w-full rounded" />
            ))}

            {(post.posts || []).length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] font-black text-amber-300 uppercase">📰 Posts & Updates</div>
                {post.posts.map((pp, i) => (
                  <div key={i} className="rounded-xl bg-slate-800/60 p-3 space-y-1.5" data-testid={`fr-post-${i}`}>
                    {pp.title && <div className="text-sm font-black">{pp.title}</div>}
                    {pp.image && <img src={abs(pp.image)} alt="" className="w-full h-40 object-cover rounded" />}
                    {pp.video && <video src={abs(pp.video)} controls className="w-full rounded" />}
                    {pp.body && <div className="text-[12px] opacity-85 whitespace-pre-wrap">{pp.body}</div>}
                    {pp.url && <a href={pp.url} target="_blank" rel="noreferrer" className="text-[12px] text-sky-300 underline">🔗 Open link</a>}
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setShowApply(true)} data-testid="fr-apply-btn"
                      className="py-3 rounded-xl bg-emerald-500 text-slate-900 font-black">📝 Apply for Franchise</button>
              {post.whatsapp && (
                <a href={`https://wa.me/${String(post.whatsapp).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                   data-testid="fr-wa-btn"
                   className="py-3 rounded-xl bg-green-600 text-white font-black text-center">📱 WhatsApp</a>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {post.url && <a href={post.url} target="_blank" rel="noreferrer" className="py-2 rounded-lg bg-slate-700 text-white text-xs font-black text-center">🌍 Website</a>}
              <button onClick={copy} data-testid="fr-copy-btn" className="py-2 rounded-lg bg-slate-700 text-white text-xs font-black">📋 Copy Link</button>
              <button onClick={waShare} data-testid="fr-wa-share-btn" className="py-2 rounded-lg bg-green-700 text-white text-xs font-black">📤 Share</button>
            </div>
            <div className="text-[10px] opacity-60 text-center">👁 {post.views || 0} views · 📝 {post.applications || 0} applications</div>
          </div>
        </div>
      </div>

      {showApply && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" onClick={() => setShowApply(false)}>
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-amber-400/40 p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="font-black">📝 Apply · {post.title}</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" data-testid="fr-apply-name" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" data-testid="fr-apply-phone" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-2 py-2 rounded bg-slate-800 text-sm" />
              <input value={form.investment_budget} onChange={(e) => setForm({ ...form, investment_budget: e.target.value })} placeholder="Budget" className="px-2 py-2 rounded bg-slate-800 text-sm" />
            </div>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Why do you want this franchise?" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
            <button onClick={submit} disabled={busy} data-testid="fr-apply-submit"
                    className="w-full py-2.5 rounded-xl bg-emerald-500 text-slate-900 font-black disabled:opacity-50">
              {busy ? '...' : '🚀 Submit Application'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
